import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../../../redux/store";
import { TenantConfigService } from "../../../../types/tenantConfig";
import { eventTypeLanguage, eventTypes } from "../../../../util";
import {
  resolveEventTypes,
  useActiveProductCategory,
} from "../../../../util/categoryConfig";

/**
 * The event types this tenant's booking flow offers — its own vocabulary where
 * it configured one, the app's standard localized list otherwise
 * (`docs/plans/tenant-configurable-event-types.md`).
 *
 * THE SINGLE READER of the tenant's `eventTypes` configuration in this app,
 * the same discipline `useDirectBookingFlow` follows next door: an absent,
 * malformed or half-written entry can never be mistaken for "configured to
 * nothing", and nothing reaches into `tenantConfig.config.eventTypes` directly.
 *
 * Absence is the documented default rather than a fallback, and it is the state
 * every LEGACY tenant is in — their configs are bundled per tenant and carry no
 * such key — as well as every self-service tenant that has not edited the list.
 * Both keep exactly today's behaviour, including today's DE/EN translation of
 * the standard vocabulary.
 *
 * ONE RULE, THREE DEPLOYABLES — mirrors `resolveEventTypes` in
 * `new-backend/admin/src/lib/serviceConfiguration.ts` (the copy pinned by the
 * backend suite) and its admin-ui twin. Tolerant on purpose: a reader faces
 * whatever the endpoint served and must still render a usable dropdown, so a
 * list with nothing usable in it resolves to the standard one rather than to an
 * empty select — an empty dropdown reads as a broken screen, not as a choice.
 */
export const useEventTypeOptions = (
  useCase: TenantConfigService = "venue"
): string[] => {
  const { i18n } = useTranslation();
  // ADR-0026 §B: category first, use case as the fallback — a municipality's
  // meeting rooms and its sports pitches do not share an event vocabulary.
  const categoryKey = useActiveProductCategory();
  const configured = useSelector((state: RootState) =>
    resolveEventTypes(state.tenantConfig?.config, useCase, categoryKey)
  );

  const language = i18n.language;

  return useMemo(() => {
    /*
     * `eventTypeLanguage`, not a plain lookup: `i18n.language` is never
     * normalized against `supportedLngs`, so it can be any string — and
     * `eventTypes['toString']` is a FUNCTION that a `??` fallback does not
     * catch, which would make the Select map over a function. That guard
     * already exists and is pinned by `getEventType.test.ts`; this reuses it
     * rather than re-deriving the same rule one file away.
     */
    const standard = eventTypes[eventTypeLanguage(language)];

    if (!Array.isArray(configured)) return standard;
    const usable = configured
      .filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0
      )
      .map((value) => value.trim());
    return usable.length > 0 ? usable : standard;
  }, [configured, language]);
};

export default useEventTypeOptions;
